import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Cloud, ArrowUpRight, X } from "lucide-react";
import { useFicStatus } from "@/context/FicStatusContext";

/**
 * Dismissible onboarding banner for users who haven't yet connected Fatture
 * in Cloud (or who selected no company on the FIC side). Renders nothing
 * when FIC is fully connected or when the user has already dismissed it
 * this session.
 */
export default function FicOnboardingBanner() {
  const { configured, connected, ready, status, loading } = useFicStatus();
  const [dismissed, setDismissed] = useState(
    () => (typeof window !== "undefined" && window.sessionStorage.getItem("fic_banner_dismissed") === "1")
  );

  if (loading || dismissed || !configured) return null;
  // Already fully connected (with a selected FIC company) → nothing to nag about
  if (ready) return null;

  const isPartial = connected && !ready; // connected but no company selected

  const dismiss = () => {
    try { window.sessionStorage.setItem("fic_banner_dismissed", "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="mb-5 rounded-xl border border-[#DBEAFE] bg-gradient-to-r from-[#EFF6FF] via-white to-white p-4 flex items-start gap-3"
      data-testid="fic-onboarding-banner"
    >
      <div className="w-10 h-10 rounded-lg bg-[#1D4ED8] text-white grid place-items-center shrink-0 shadow-sm shadow-blue-600/20">
        <Cloud className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#1D4ED8]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#1D4ED8]" />
          {isPartial ? "Collegamento FIC quasi pronto" : "Suggerito"}
        </div>
        <div className="mt-1 font-display font-bold text-base text-[#0A0A0A]">
          {isPartial
            ? "Seleziona la company Fatture in Cloud"
            : "Collega Fatture in Cloud in 30 secondi"}
        </div>
        <p className="text-xs text-[#4A4A4A] mt-1 max-w-2xl">
          {isPartial
            ? "Hai autorizzato l'accesso ma manca la scelta dell'azienda su cui creare le autofatture. Selezionala per abilitare l'invio."
            : "Invia le autofatture direttamente da qui a Fatture in Cloud senza doverle re-inserire. Utile se il tuo commercialista lavora già su FIC."}
        </p>
        <Link
          to="/app/integrations"
          data-testid="fic-onboarding-cta"
          className="mt-2.5 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#1D4ED8] transition-colors"
        >
          {isPartial ? "Scegli azienda FIC" : "Collega ora"}
          <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
      <button
        onClick={dismiss}
        data-testid="fic-banner-dismiss"
        className="p-1 rounded-md text-[#737373] hover:bg-white hover:text-[#0A0A0A] shrink-0"
        title="Chiudi (verrà mostrato di nuovo al prossimo accesso)"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
